import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TokenManagerService } from '../services/token-manager.service';
import { firstValueFrom } from 'rxjs';

declare var ConsentSDK: any;

// Type definitions for consent data
interface CompanyDetails {
  companyNumber?: string;
  companyName?: string;
}

interface CustomerDetails {
  email: string;
  firstName?: string;
  lastName?: string;
  signUpType?: 'individual' | 'company';
  companyDetails?: CompanyDetails;
  phoneNumber?: string;
  role?: string;
}

interface Meter {
  mpxn: string;
  fuelType: 'Elec' | 'Gas';
  address?: string;
}

interface MeterDetails {
  meters: Meter[];
}

// Output event interface
interface ConsentResult {
  consent: 'SUCCESS' | 'ERROR';
  value: string; // shareToken on success, errorCode on error
}

@Component({
  selector: 'app-consent-from-perse-wallet',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatCardModule,
    MatProgressSpinnerModule
  ],
  template: `
    <mat-card class="consent-card">
      <!-- <mat-card-header>
        <mat-card-title>Consent Management</mat-card-title>
      </mat-card-header> -->
      
      <mat-card-content>
        <div class="consent-description">
          <h3>Perse ConsentWallet</h3>
          <p *ngIf="!isRequestor">
            The Perse ConsentWallet provides a secure and transparent way to manage your data consent preferences.
            By using this service, you can control how your energy usage data is shared and utilised.
          </p>
          <p *ngIf="!isRequestor">
            You will be asked to create a wallet if you haven't already signed up for Perse Wallet. Once the wallet is created, you can use it to store, share, and manage your consent.
            You may also use the same wallet to share your consent with other services that support Perse ConsentWallet integration.
          </p>
          <p *ngIf="isRequestor">
             The Perse ConsentWallet provides a secure and transparent way to manage your data consent preferences.
            By using this service, your customers can control how their energy usage data is shared and utilised.
          </p>
          <p *ngIf="isRequestor">
            Your customers will receive an email with a secure link to complete the consent process. You will be able to start accessing their data once they have provided their consent.
        </div>
      </mat-card-content>
      
      <mat-card-actions>
        <button 
          mat-flat-button 
          color="primary" 
          (click)="onConsentAction()"
          [disabled]="isLoading"
          class="consent-button">
          <!-- <mat-spinner *ngIf="isLoading" diameter="20" class="button-spinner"></mat-spinner> -->
          <span *ngIf="!isLoading">{{ isRequestor ? 'Send consent request via email' : 'Click here to provide consent' }}</span>
          <span *ngIf="isLoading">{{ isRequestor ? 'Sending request...' : 'Processing...' }}</span>
        </button>
      </mat-card-actions>
    </mat-card>
  `,
  styles: [`
    .consent-card {
      max-width: 600px;
      margin: 20px auto;
    }
    
    .consent-description {
      margin: 16px 0;
    }
    
    .consent-description h3 {
      color: #1976d2;
      margin-bottom: 16px;
      font-weight: 500;
    }
    
    .consent-description p {
      line-height: 1.6;
      margin-bottom: 12px;
      color: #666;
    }
    
    .consent-button {
      width: 100%;
      padding: 12px;
      font-size: 16px;
    }
    
    mat-card-actions {
      padding: 16px;
    }

    .button-spinner {
      margin-right: 8px;
    }
  `]
})
export class ConsentFromPerseWalletComponent {
  
  @Input() isRequestor: boolean = false; // If true, send email. If false, open consent as popup
  @Input() customerDetails?: CustomerDetails;
  @Input() meterDetails?: MeterDetails;
  @Output() consentResult = new EventEmitter<ConsentResult>();

  isLoading = false;

  constructor(private tokenManagerService: TokenManagerService) { }

  /**
   * Handles the consent action button click
   * Initiates consent request using ConsentSDK
   */
  async onConsentAction(): Promise<void> {
    console.log('Consent action triggered - Initiating Perse ConsentWallet integration');
    
    this.isLoading = true;
    try {
      await this.requestConsentWithRetry();
    } catch (error: any) {
      console.error('Consent request failed after retry:', error);
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Requests consent with automatic token refresh on UNAUTHORIZED error
   */
  private async requestConsentWithRetry(): Promise<void> {
    let token = await firstValueFrom(this.tokenManagerService.getNewToken());
    
    try {
      await this.performConsentRequest(token);
    } catch (error: any) {
      if (error.code === 'UNAUTHORIZED') {
        console.log('Token unauthorised, getting new token and retrying...');
        // Get a new token and retry
        token = await firstValueFrom(this.tokenManagerService.getNewToken());
        await this.performConsentRequest(token);
      } else {
        throw error; // Re-throw non-unauthorized errors
      }
    }
  }

  /**
   * Performs the actual consent request
   */
  private async performConsentRequest(token: string): Promise<void> {
    const consentSDK = new ConsentSDK(token);

    try {
      let referenceId: string;
      
      if (this.isRequestor) {
        // For TPI scenario - request consent via email
        referenceId = await consentSDK.requestConsentViaEmail(this.customerDetails, this.meterDetails, {scope: 'HH'});
        console.log('Consent request sent via email! Request ID:', referenceId);
      } else {
        // For self consent scenario - open consent popup
        referenceId = await consentSDK.requestConsent(this.customerDetails, this.meterDetails, {scope: 'HH'});
        console.log('Consent approved! Reference ID:', referenceId);
      }
      
      // Emit success result
      this.consentResult.emit({
        consent: 'SUCCESS',
        value: referenceId
      });
      
    } catch (error: any) {
      console.error('Consent request failed:', error);
      
      let errorCode = 'UNKNOWN_ERROR';
      
      // Handle different error types
      switch (error.code) {
        case 'REQUEST_CANCELLED':
          console.log('Customer cancelled the consent request');
          errorCode = 'REQUEST_CANCELLED';
          break;
        case 'INVALID_CUSTOMER_DETAILS':
          console.log('Customer details are invalid:', error.message);
          errorCode = 'INVALID_CUSTOMER_DETAILS';
          break;
        case 'INVALID_METER_DETAILS':
          console.log('Meter details are invalid:', error.message);
          errorCode = 'INVALID_METER_DETAILS';
          break;
        case 'UNAUTHORIZED':
          console.log('Token is unauthorised:', error.message);
          errorCode = 'UNAUTHORIZED';
          throw error; // Re-throw to trigger retry logic
        default:
          console.log('An unexpected error occurred:', error.message);
          errorCode = error.code || 'UNKNOWN_ERROR';
      }
      
      // Emit error result (except for UNAUTHORIZED which will be retried)
      if (error.code !== 'UNAUTHORIZED') {
        this.consentResult.emit({
          consent: 'ERROR',
          value: errorCode
        });
      }
      
      throw error; // Re-throw all errors to be handled by retry logic
    }
  }
}
